import * as React from "react";
import * as moment from "moment";
import {Subscription} from "rxjs";
import {getFirstInstalmentDueDate} from "@ferry/plugin-ferry-journey";

interface IPaymentScheduleProps {
    isOpen: boolean;
    instalments: any[]; // instalments with possible gaps
    interaction: any;
    instalmentDueDates?: boolean;
}

interface IPaymentScheduleState {
    lastDoneInstalmentIndex: number;
}

export default class DirectDebitPaymentSchedule extends React.Component<IPaymentScheduleProps, IPaymentScheduleState> {

    private readonly progressContainerRef: React.RefObject<HTMLDivElement | null> = React.createRef();
    private readonly lastDoneInstalmentDotRef: React.RefObject<HTMLDivElement | null> = React.createRef();

    private windowResizeEventSubscription: Subscription;

    constructor(props: IPaymentScheduleProps) {
        super(props);

        this.state = {
            lastDoneInstalmentIndex: -1
        };
    }

    public componentWillUnmount() {
        if (this.windowResizeEventSubscription) {
            this.windowResizeEventSubscription.unsubscribe();
        }
    }

    public render() {
        return (
            <div className={`payment-schedule ${this.props.isOpen ? "payment-schedule--open" : ""}`}>
                <div
                    className="payment-schedule__progress-container"
                    ref={this.progressContainerRef}
                >
                    <div className="payment-schedule__progress">
                        <div className="payment-schedule__progress-bar"/>
                    </div>
                </div>
                <div className="payment-schedule__dot-container">
                    {this.renderPaymentSchedule()}
                </div>
            </div>
        );
    }

    private renderPaymentSchedule() {
        if (this.props.isOpen) {
            if (this.props.instalmentDueDates) {
                return this.renderVerticalDotsWithInstalmentDueDate();
            } else {
                return this.renderVerticalDotsWithSchedule();
            }
        } else {
            return this.renderHorizontalDots();
        }
    }

    private renderHorizontalDots = (): React.ReactNode => {
        return this.props.instalments.map((instalment, index) => {
            const isDone = index <= this.state.lastDoneInstalmentIndex;

            return (
                <div
                    key={instalment.month}
                    className={`progress-dot${isDone ? " progress-dot--paid" : ""}`}
                    ref={index === this.state.lastDoneInstalmentIndex ? this.lastDoneInstalmentDotRef : undefined}
                />
            );
        });
    }

    private renderVerticalDotsWithSchedule = (): React.ReactNode => {

        const firstInstalmentDueDate:moment.Moment = getFirstInstalmentDueDate(this.props.interaction);

        return this.props.instalments.map((instalment, index, instalments) => {

            const amount = instalment.principal + instalment.interest;

            return (
                <React.Fragment key={instalment.month}>
                    <div className="payment-schedule__grid-item instalment-text-color">
                        {index === 0 ?
                            firstInstalmentDueDate.format("DD MMM YYYY")
                            : firstInstalmentDueDate.add(1, 'months').format("DD MMM YYYY")}
                    </div>
                    {this.dotAndAmount(index, amount)}
                </React.Fragment>
            );
        });
    }

    private renderVerticalDotsWithInstalmentDueDate = (): React.ReactNode => {
        return this.props.instalments.map((instalment, index, instalments) => {

            const amount = instalment.principal + instalment.interest;

            return (
                <React.Fragment key={instalment.month}>
                    <div className="payment-schedule__grid-item instalment-text-color">
                        {moment(instalment.dueDate).format("DD MMM YYYY")}
                    </div>
                    {this.dotAndAmount(index, amount)}
                </React.Fragment>
            );
        });
    }

    private dotAndAmount(index: number, amount: any) {
        return <>
            <div className="payment-schedule__grid-item">
                <div
                    className="progress-dot"
                    ref={index === this.state.lastDoneInstalmentIndex ? this.lastDoneInstalmentDotRef : undefined}
                />
            </div>
            <div className="payment-schedule__grid-item payment-plan-item instalment-text-color">
                {`£${amount.toFixed(2)}`} {index === 0 ? "(1st pay)" : ""}
            </div>
        </>;
    }
}
